import { apiGet, apiPatch, apiPost, apiPut } from "./client";

export type ProductRow = {
  id: string;
  name: string;
  sku: string | null;
  barcode: string | null;
  kind: "stocked" | "service";
  trackLot: boolean;
  trackSerial: boolean;
  /** Null when redacted — plan/38 master.product.cost.view */
  avgCost: string | null;
  deactivatedAt: string | null;
  updatedAt: string;
};

export type PriceListRow = {
  id: string;
  code: string;
  name: string;
  isDefaultPos: boolean;
  deactivatedAt?: string | null;
};

export type PriceListItemRow = {
  productId: string;
  productName: string;
  sku: string | null;
  price: string;
};

export type ProductListPage = {
  products: ProductRow[];
  company: { taxPriceMode: string; currencyCode: string };
  hasMore?: boolean;
  nextCursor?: string | null;
  total?: number;
};

export type ProductListSort = "name" | "sku" | "kind" | "avgCost";

export function listProducts(
  companyRef: string,
  opts?: {
    includeDeactivated?: boolean;
    /** When set, server paginates (plan/34). Omit for full picker lists. */
    q?: string;
    limit?: number;
    cursor?: string | null;
    sort?: ProductListSort;
    sortDir?: "asc" | "desc";
    signal?: AbortSignal;
  },
) {
  const params = new URLSearchParams();
  if (opts?.includeDeactivated) params.set("all", "1");
  if (opts?.q?.trim()) params.set("q", opts.q.trim());
  if (opts?.limit != null) params.set("limit", String(opts.limit));
  if (opts?.cursor) params.set("cursor", opts.cursor);
  if (opts?.sort && opts.sort !== "name") params.set("sort", opts.sort);
  if (opts?.sortDir === "desc") params.set("sortDir", "desc");
  const qs = params.toString();
  return apiGet<ProductListPage>(
    `/c/${companyRef}/products${qs ? `?${qs}` : ""}`,
    { signal: opts?.signal },
  );
}

export function createProduct(
  companyRef: string,
  body: {
    name: string;
    sku?: string;
    barcode?: string;
    kind?: "stocked" | "service";
    posPrice?: string;
    trackLot?: boolean;
    trackSerial?: boolean;
  },
) {
  return apiPost<{ product: ProductRow }>(`/c/${companyRef}/products`, body);
}

export type ProductDetail = {
  product: ProductRow & {
    notes?: string | null;
  };
  prices: Array<{
    priceListId: string;
    listCode: string;
    listName: string;
    price: string;
  }>;
  balances: Array<{
    warehouseId: string;
    warehouseCode: string;
    qty: string;
  }>;
};

export function getProduct(companyRef: string, productId: string) {
  return apiGet<ProductDetail>(`/c/${companyRef}/products/${productId}`);
}

/** Lot / serial balances + recent moves — plan/31 */
export function fetchProductLots(companyRef: string, productId: string) {
  return apiGet<{
    product: {
      id: string;
      name: string;
      sku: string | null;
      trackLot: boolean;
      trackSerial: boolean;
      kind: string;
    };
    lots: Array<{
      warehouseId: string;
      warehouseCode: string;
      lotCode: string | null;
      serialCode: string | null;
      qty: string;
    }>;
    moves: Array<{
      id: string;
      warehouseId: string;
      warehouseCode: string;
      qty: string;
      reason: string;
      docType: string | null;
      docId: string | null;
      lotCode: string | null;
      serialCode: string | null;
      createdAt: string;
    }>;
  }>(`/c/${companyRef}/products/${productId}/lots`);
}

export function updateProduct(
  companyRef: string,
  productId: string,
  body: {
    name?: string;
    sku?: string | null;
    barcode?: string | null;
    kind?: "stocked" | "service";
    trackLot?: boolean;
    trackSerial?: boolean;
    reactivate?: boolean;
  },
) {
  return apiPatch<{ product: ProductRow }>(
    `/c/${companyRef}/products/${productId}`,
    body,
  );
}

export function deactivateProduct(companyRef: string, productId: string) {
  return apiPost<{ product: { id: string; deactivated: boolean } }>(
    `/c/${companyRef}/products/${productId}/deactivate`,
  );
}

/** May apply immediately or queue master.product.cost change request. */
export function adjustProductStock(
  companyRef: string,
  productId: string,
  body: {
    qtyDelta: string;
    unitCost?: string;
    warehouseId?: string;
    memo?: string;
    confirmOversell?: boolean;
    idempotencyKey?: string;
  },
) {
  return apiPost<
    | {
        applied: true;
        adjustment: {
          id: string;
          direction: "in" | "out";
          qtyDelta: string;
          unitCost: string;
          inventoryAmount: string;
          qtyAfter: string;
          oversell: boolean;
          memo: string;
        };
        replayed?: boolean;
      }
    | {
        applied: false;
        changeRequest: {
          id: string;
          status: string;
          permissionKey: string;
          category: string;
        };
        replayed?: boolean;
      }
  >(`/c/${companyRef}/products/${productId}/adjustments`, body);
}

export type WarehouseRow = {
  id: string;
  code: string;
  name: string;
  isDefault: boolean;
  branchId?: string | null;
  deactivatedAt?: string | null;
};

export function listWarehouses(
  companyRef: string,
  opts?: { includeDeactivated?: boolean },
) {
  const q = opts?.includeDeactivated ? "?all=1" : "";
  return apiGet<{ warehouses: WarehouseRow[] }>(
    `/c/${companyRef}/warehouses${q}`,
  );
}

export function createWarehouse(
  companyRef: string,
  body: {
    name: string;
    code?: string;
    isDefault?: boolean;
    branchId?: string | null;
  },
) {
  return apiPost<{ warehouse: WarehouseRow }>(
    `/c/${companyRef}/warehouses`,
    body,
  );
}

export function updateWarehouse(
  companyRef: string,
  warehouseId: string,
  body: { name?: string; isDefault?: boolean; reactivate?: boolean },
) {
  return apiPatch<{ warehouse: WarehouseRow }>(
    `/c/${companyRef}/warehouses/${warehouseId}`,
    body,
  );
}

export function deactivateWarehouse(companyRef: string, warehouseId: string) {
  return apiPost<{ warehouse: { id: string; deactivated: boolean } }>(
    `/c/${companyRef}/warehouses/${warehouseId}/deactivate`,
  );
}

export type InventoryBalanceRow = {
  productId: string;
  productName: string;
  sku: string | null;
  barcode: string | null;
  kind: "stocked" | "service";
  /** Null when redacted — plan/38 */
  avgCost: string | null;
  warehouseId: string;
  warehouseCode: string;
  warehouseName: string;
  qty: string;
  updatedAt: string;
};

export type InventoryListPage = {
  balances: InventoryBalanceRow[];
  /** Present when `lowStock=1` — org threshold used for the filter (plan/44). */
  lowStockThreshold?: string;
  hasMore?: boolean;
  nextCursor?: string | null;
  total?: number;
};

export type InventoryListSort =
  | "productName"
  | "sku"
  | "warehouseCode"
  | "qty"
  | "avgCost";

export function listInventory(
  companyRef: string,
  opts?: {
    warehouseId?: string;
    q?: string;
    /** Owner glance Low stock — on-hand ≤ org threshold (plan/44) */
    lowStock?: boolean;
    limit?: number;
    cursor?: string | null;
    sort?: InventoryListSort;
    sortDir?: "asc" | "desc";
    signal?: AbortSignal;
  },
) {
  const params = new URLSearchParams();
  if (opts?.warehouseId) params.set("warehouseId", opts.warehouseId);
  if (opts?.q?.trim()) params.set("q", opts.q.trim());
  if (opts?.lowStock) params.set("lowStock", "1");
  if (opts?.limit != null) params.set("limit", String(opts.limit));
  if (opts?.cursor) params.set("cursor", opts.cursor);
  if (opts?.sort && opts.sort !== "productName") params.set("sort", opts.sort);
  if (opts?.sortDir === "desc") params.set("sortDir", "desc");
  const qs = params.toString();
  return apiGet<InventoryListPage>(
    `/c/${companyRef}/inventory${qs ? `?${qs}` : ""}`,
    { signal: opts?.signal },
  );
}

export function listPriceLists(
  companyRef: string,
  opts?: { includeDeactivated?: boolean },
) {
  const q = opts?.includeDeactivated ? "?all=1" : "";
  return apiGet<{ priceLists: PriceListRow[] }>(
    `/c/${companyRef}/price-lists${q}`,
  );
}

export function createPriceList(
  companyRef: string,
  body: { name: string; code?: string; isDefaultPos?: boolean },
) {
  return apiPost<{ priceList: PriceListRow }>(
    `/c/${companyRef}/price-lists`,
    body,
  );
}

export function updatePriceList(
  companyRef: string,
  listId: string,
  body: { name?: string; isDefaultPos?: boolean; reactivate?: boolean },
) {
  return apiPatch<{ priceList: PriceListRow }>(
    `/c/${companyRef}/price-lists/${listId}`,
    body,
  );
}

export function deactivatePriceList(companyRef: string, listId: string) {
  return apiPost<{ priceList: { id: string; deactivated: boolean } }>(
    `/c/${companyRef}/price-lists/${listId}/deactivate`,
  );
}

export function getPriceList(companyRef: string, listId: string) {
  return apiGet<{
    priceList: PriceListRow;
    items: PriceListItemRow[];
  }>(`/c/${companyRef}/price-lists/${listId}`);
}

/** May apply immediately or queue change requests (plan/16 master.product.price). */
export function upsertPriceListItems(
  companyRef: string,
  listId: string,
  items: Array<{ productId: string; price: string }>,
) {
  return apiPut<
    | { applied: true; upserted: number }
    | {
        applied: false;
        queued: number;
        changeRequests: Array<{
          id: string;
          status: string;
          permissionKey: string;
          category: string;
        }>;
      }
  >(`/c/${companyRef}/price-lists/${listId}/items`, { items });
}
